import { Input } from "antd";
import { TextAreaRef } from "antd/es/input/TextArea";
import React, { useState, useRef, useEffect } from "react";

interface ResizableTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  minHeight?: number;
  maxHeight?: number;
  defaultHeight?: number;
  size?: "small" | "middle" | "large";
  onSizeChange?: (height: number) => void;
}

let startY: number;
let startH: number;
const maxHeight = document.documentElement.clientHeight / 2;

const ResizableTextarea = React.forwardRef<TextAreaRef, ResizableTextareaProps>(
  ({ minHeight = 60, defaultHeight = 60, className = "", onSizeChange, ...props }, ref) => {
    const [height, setHeight] = useState(defaultHeight);
    const textareaRef = useRef<TextAreaRef>(null);
    const handleRef = useRef<HTMLDivElement>(null);
    const isResizing = useRef(false);

    useEffect(() => {
      setHeight(defaultHeight);
    }, [defaultHeight]);

    // 将外部传入的 ref 和内部的 ref 关联起来
    useEffect(() => {
      if (ref) {
        if (typeof ref === "function") {
          ref(textareaRef.current);
        } else {
          ref.current = textareaRef.current;
        }
      }
    }, [ref]);

    // 处理鼠标按下事件，开始调整高度
    const handleMouseDown = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      e.preventDefault();
      startY = e.clientY;
      startH =
        textareaRef.current!.resizableTextArea!.textArea.getBoundingClientRect()
          .height;
      isResizing.current = true;
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    // 处理鼠标移动事件，调整高度
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current || !textareaRef.current) return;

      const newHeight = startY - e.clientY + startH;

      if (newHeight >= minHeight && newHeight <= maxHeight) {
        // console.log(newHeight, startY, e.clientY);
        // textareaRef.current.resizableTextArea!.textArea.style.height = `${newHeight}px`;
        requestAnimationFrame(() => {
          setHeight(newHeight);
          onSizeChange?.(newHeight);
        });
      }
    };

    // 处理鼠标释放事件，结束调整高度
    const handleMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    // 应用计算后的高度样式
    const textareaStyle = {
      height: `${height}px`,
      resize: "none", // 禁用原生调整功能
      boxSizing: "border-box",
    };

    // 处理手柄样式
    const handleStyle = {
      cursor: "ns-resize",
      height: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div ref={handleRef} style={handleStyle} onMouseDown={handleMouseDown}>
          <div
            style={{
              width: "20px",
              height: "3px",
              backgroundColor: "#ccc",
              borderRadius: "1px",
            }}
          />
        </div>
        <Input.TextArea
          ref={textareaRef}
          style={textareaStyle as any}
          className={`${className}`}
          onClick={e=>{
            e.preventDefault();
            e.stopPropagation();
          }}
          {...props}
        />
      </div>
    );
  }
);

ResizableTextarea.displayName = "ResizableTextarea";

export default ResizableTextarea;
